/**
 * Katana MRP API Service
 * Handles all communication with Katana API via Cloudflare Pages Functions
 */

/// <reference path="../vite-env.d.ts" />

import type {
  PurchaseOrder,
  PurchaseOrderFilters,
  PurchaseOrderSortOptions,
  Supplier,
  Variant,
  Product,
  Material,
  Location,
  Inventory,
  InventoryMetrics,
  DashboardMetrics,
  KatanaSettings,
  KatanaApiResponse,
  PaginatedResponse,
  ManufacturingOrder,
  ManufacturingOrderFilters,
  ManufacturingOrderSortOptions,
  ManufacturingOrderMetrics,
  // New imports for all additional resources
  Customer,
  CustomerFilters,
  CustomerSortOptions,
  SalesOrder,
  SalesOrderFilters,
  SalesOrderSortOptions,
  SalesOrderFulfillment,
  SalesReturn,
} from '@/types/katana';

// Global shop cache to persist across service instances
let globalShopCache: string | null = null;

// Force refresh the shop parameter detection
function refreshShopDetection(): void {
  globalShopCache = null;
  console.log('🔄 Shop cache cleared - will re-detect on next request');
}

class KatanaApiService {
  private baseUrl = '/api/katana';

  /**
   * Get shop parameter from URL parameters (Shopify Managed Installation provides this)
   */
  private getShopParameter(): string {
    // Return cached shop if available and not dev mode
    if (globalShopCache && globalShopCache !== 'dev-shop.myshopify.com') {
      console.log('✅ Shop from cache:', globalShopCache);
      return globalShopCache;
    }
    
    // Get shop from URL parameters - Shopify passes this automatically
    const urlParams = new URLSearchParams(window.location.search);
    const shopFromUrl = urlParams.get('shop');
    
    if (shopFromUrl && shopFromUrl.endsWith('.myshopify.com')) {
      console.log('✅ Shop from URL:', shopFromUrl);
      globalShopCache = shopFromUrl; // Cache globally
      localStorage.setItem('shopify_shop_domain', shopFromUrl); // Persist
      return shopFromUrl;
    }
    
    // Try to get shop domain from localStorage FIRST (persists across page reloads)
    const storedShop = localStorage.getItem('shopify_shop_domain');
    if (storedShop && storedShop.endsWith('.myshopify.com') && storedShop !== 'dev-shop.myshopify.com') {
      console.log('✅ Shop from localStorage:', storedShop);
      globalShopCache = storedShop;
      return storedShop;
    }
    
    // Fallback to global config set by App Bridge initialization
    const globalWindow = window as any;
    if (globalWindow.shopifyUrlParams?.shop && 
        globalWindow.shopifyUrlParams.shop !== 'dev-shop.myshopify.com') {
      console.log('✅ Shop from global config:', globalWindow.shopifyUrlParams.shop);
      globalShopCache = globalWindow.shopifyUrlParams.shop; // Cache globally
      localStorage.setItem('shopify_shop_domain', globalWindow.shopifyUrlParams.shop); // Persist
      return globalWindow.shopifyUrlParams.shop;
    }
    
    // Check if we're in a real Shopify environment by looking for App Bridge
    if (globalWindow.shopify && (globalWindow.shopify.AppBridge || globalWindow.shopify.app)) {
      // Try to get shop from the current domain if we're embedded
      const hostname = window.location.hostname;
      if (hostname.includes('.myshopify.com')) {
        console.log('✅ Shop from hostname:', hostname);
        globalShopCache = hostname; // Cache globally
        localStorage.setItem('shopify_shop_domain', hostname); // Persist
        return hostname;
      }
    }
    
    // For embedded apps, try to get shop from window.location.ancestorOrigins or document.referrer
    if (window.top !== window) {
      try {
        // Try to extract shop from referrer or parent window
        const referrer = document.referrer;
        if (referrer && referrer.includes('.myshopify.com')) {
          const url = new URL(referrer);
          const shopFromReferrer = url.hostname;
          if (shopFromReferrer.endsWith('.myshopify.com')) {
            console.log('✅ Shop from referrer:', shopFromReferrer);
            globalShopCache = shopFromReferrer; // Cache globally
            localStorage.setItem('shopify_shop_domain', shopFromReferrer); // Persist
            return shopFromReferrer;
          }
        }
      } catch (error) {
        console.log('Could not extract shop from referrer:', error);
      }
    }
    
    // Development fallback only if we can't detect a real shop
    console.log('🔧 Development mode - no shop parameter detected');
    console.log('URL params:', window.location.search);
    console.log('Cached shop:', globalShopCache);
    console.log('Stored shop:', localStorage.getItem('shopify_shop_domain'));
    return 'dev-shop.myshopify.com';
  }

  /**
   * Make authenticated request to Katana API via Cloudflare Pages Functions
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Add shop parameter to the endpoint
    const shop = this.getShopParameter();
    console.log(`🛒 API Request to ${endpoint} using shop: ${shop}`);
    const separator = endpoint.includes('?') ? '&' : '?';
    const url = `${this.baseUrl}${endpoint}${separator}shop=${encodeURIComponent(shop)}`;
    
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as any;
        throw new KatanaApiError(
          errorData.code || 'API_ERROR',
          errorData.message || `HTTP ${response.status}: ${response.statusText}`,
          errorData.details
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof KatanaApiError) {
        throw error;
      }
      
      throw new KatanaApiError(
        'NETWORK_ERROR',
        'Failed to connect to Katana API',
        error
      );
    }
  }

  /**
   * Get dashboard metrics and summary data
   */
  async getDashboardMetrics(
    dateFrom?: string,
    dateTo?: string,
    maxRecords?: number
  ): Promise<DashboardMetrics> {
    const params = new URLSearchParams();
    
    if (dateFrom) {
      params.append('date_from', dateFrom);
    }
    if (dateTo) {
      params.append('date_to', dateTo);
    }
    if (maxRecords) {
      params.append('max_records', maxRecords.toString());
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/dashboard?${queryString}` : '/dashboard';
    
    return this.makeRequest<DashboardMetrics>(endpoint);
  }

  /**
   * Get purchase orders with filtering and pagination
   */
  async getPurchaseOrders(
    filters?: PurchaseOrderFilters,
    sort?: PurchaseOrderSortOptions,
    page = 1,
    perPage = 25
  ): Promise<PaginatedResponse<PurchaseOrder>> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    // Add filters
    if (filters) {
      if (filters.status?.length) {
        params.append('status', filters.status.join(','));
      }
      if (filters.entity_type?.length) {
        params.append('entity_type', filters.entity_type.join(','));
      }
      if (filters.supplier_id?.length) {
        params.append('supplier_id', filters.supplier_id.join(','));
      }
      if (filters.currency?.length) {
        params.append('currency', filters.currency.join(','));
      }
      if (filters.date_from) {
        params.append('date_from', filters.date_from);
      }
      if (filters.date_to) {
        params.append('date_to', filters.date_to);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
    }

    // Add sorting
    if (sort) {
      params.append('sort_by', sort.field);
      params.append('sort_direction', sort.direction);
    }

    return this.makeRequest<PaginatedResponse<PurchaseOrder>>(
      `/purchase-orders?${params.toString()}`
    );
  }

  /**
   * Get a specific purchase order by ID
   */
  async getPurchaseOrder(id: number): Promise<PurchaseOrder> {
    return this.makeRequest<PurchaseOrder>(`/purchase-orders/${id}`);
  }

  /**
   * Get all suppliers (cached for performance)
   */
  async getSuppliers(): Promise<Supplier[]> {
    const response = await this.makeRequest<KatanaApiResponse<Supplier[]>>('/suppliers');
    
    console.log('🔍 getSuppliers raw response:', {
      response,
      hasData: 'data' in response,
      dataType: typeof response.data,
      isArray: Array.isArray(response.data),
      responseKeys: Object.keys(response),
      sampleData: Array.isArray(response.data) ? response.data.slice(0, 2) : response.data
    });
    
    // Handle nested data structure from Katana API
    // Katana API returns { data: [items], pagination: {...} }
    // Our API wraps this in { data: katanaResponse, success: true, cached: false }
    let suppliers: Supplier[] = [];
    
    if (Array.isArray(response.data)) {
      // Direct array (development/cached data)
      suppliers = response.data;
    } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      // Nested Katana API response format
      const nestedData = (response.data as any).data;
      suppliers = Array.isArray(nestedData) ? nestedData : [];
    } else if (response.data && typeof response.data === 'object') {
      // Fallback: check if response.data has suppliers directly
      suppliers = [];
    }
    
    console.log('✅ getSuppliers processed:', {
      originalType: typeof response.data,
      suppliersCount: suppliers.length,
      sampleSupplier: suppliers[0] || null
    });
    
    return suppliers;
  }

  /**
   * Get a specific supplier by ID
   */
  async getSupplier(id: number): Promise<Supplier> {
    return this.makeRequest<Supplier>(`/suppliers/${id}`);
  }

  /**
   * Get all variants (products/materials)
   */
  async getVariants(page = 1, perPage = 100): Promise<PaginatedResponse<Variant>> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    return this.makeRequest<PaginatedResponse<Variant>>(
      `/variants?${params.toString()}`
    );
  }

  /**
   * Get all products
   */
  async getProducts(page = 1, perPage = 100): Promise<PaginatedResponse<Product>> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    return this.makeRequest<PaginatedResponse<Product>>(
      `/products?${params.toString()}`
    );
  }

  /**
   * Get all materials  
   */
  async getMaterials(page = 1, perPage = 100): Promise<PaginatedResponse<Material>> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    return this.makeRequest<PaginatedResponse<Material>>(
      `/materials?${params.toString()}`
    );
  }

  /**
   * Get all locations and identify the primary one
   */
  async getLocations(): Promise<Location[]> {
    return this.makeRequest<{ data: Location[] }>('/locations')
      .then(response => {
        console.log('🔍 getLocations raw response:', {
          response,
          hasData: 'data' in response,
          dataType: typeof response.data,
          isArray: Array.isArray(response.data),
          responseKeys: Object.keys(response),
          sampleData: Array.isArray(response.data) ? response.data.slice(0, 2) : response.data
        });
        
        // Handle nested data structure from Katana API
        // Katana API returns { data: [items], pagination: {...} }
        // Our API wraps this in { data: katanaResponse, success: true, cached: false }
        let locations: Location[] = [];
        
        if (Array.isArray(response.data)) {
          // Direct array (development/cached data)
          locations = response.data;
        } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
          // Nested Katana API response format
          const nestedData = (response.data as any).data;
          locations = Array.isArray(nestedData) ? nestedData : [];
        } else if (response.data && typeof response.data === 'object') {
          // Fallback: empty array
          locations = [];
        }
        
        console.log('✅ getLocations processed:', {
          originalType: typeof response.data,
          locationsCount: locations.length,
          sampleLocation: locations[0] || null
        });
        
        return locations;
      });
  }

  /**
   * Get current inventory for a specific location only
   */
  async getInventoryByLocation(locationId: number): Promise<{ data: Inventory[]; locations: Location[] }> {
    const params = new URLSearchParams();
    params.append('location_id', locationId.toString());
    
    const queryString = params.toString();
    const endpoint = `/inventory?${queryString}`;
    
    return this.makeRequest<{ data: Inventory[]; locations: Location[] }>(endpoint);
  }

  /**
   * Get aggregated inventory metrics for a specific location
   */
  async getInventoryMetricsByLocation(locationId: number): Promise<InventoryMetrics> {
    const params = new URLSearchParams();
    params.append('metrics', 'true');
    params.append('location_id', locationId.toString());
    
    const queryString = params.toString();
    const endpoint = `/inventory?${queryString}`;
    
    console.log(`🌐 Making request to: ${endpoint}`);
    const result = await this.makeRequest<InventoryMetrics>(endpoint);
    
    console.log('🔍 Raw API response for location', locationId, ':', {
      total_value: result.total_value_in_stock,
      total_value_type: typeof result.total_value_in_stock,
      is_array: Array.isArray(result.total_value_in_stock),
      first_50_chars: typeof result.total_value_in_stock === 'string' 
        ? (result.total_value_in_stock as string).substring(0, 50) + '...'
        : result.total_value_in_stock
    });
    
    return result;
  }

  /**
   * Get inventory metrics for all locations (loads primary location first)
   */
  async getAllLocationsInventoryMetrics(): Promise<{
    locations: Location[];
    primaryLocation: Location | null;
    allMetrics: InventoryMetrics;
    locationMetrics: Record<number, InventoryMetrics>;
  }> {
    // First, get all locations
    const locations = await this.getLocations();
    const primaryLocation = locations.find(loc => loc.is_primary) || locations[0] || null;
    
    if (!primaryLocation) {
      throw new Error('No locations found');
    }

    // Get primary location metrics first (for immediate display)
    const primaryMetrics = await this.getInventoryMetricsByLocation(primaryLocation.id);
    
    // Initialize response with primary location data
    const locationMetrics: Record<number, InventoryMetrics> = {
      [primaryLocation.id]: primaryMetrics
    };

    // Calculate aggregated metrics from primary location (will be updated as more locations load)
    const allMetrics: InventoryMetrics = {
      total_value_in_stock: primaryMetrics.total_value_in_stock,
      total_quantity_in_stock: primaryMetrics.total_quantity_in_stock,
      by_location: { ...primaryMetrics.by_location },
      locations
    };

    return {
      locations,
      primaryLocation,
      allMetrics,
      locationMetrics
    };
  }

  /**
   * Load additional location metrics (for progressive loading)
   */
  async getAdditionalLocationMetrics(locationIds: number[]): Promise<Record<number, InventoryMetrics>> {
    const results: Record<number, InventoryMetrics> = {};
    
    // Load locations one by one to avoid overwhelming the API
    for (const locationId of locationIds) {
      try {
        const metrics = await this.getInventoryMetricsByLocation(locationId);
        results[locationId] = metrics;
        
        // Small delay between requests to be API-friendly
        if (locationIds.length > 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`Failed to load metrics for location ${locationId}:`, error);
      }
    }
    
    return results;
  }

  /**
   * Legacy method for backward compatibility - now defaults to primary location
   * @deprecated Use getAllLocationsInventoryMetrics() or getInventoryMetricsByLocation() instead
   */
  async getInventoryMetrics(locationId?: number): Promise<InventoryMetrics> {
    if (locationId) {
      return this.getInventoryMetricsByLocation(locationId);
    }
    
    // Default to primary location for backward compatibility
    const result = await this.getAllLocationsInventoryMetrics();
    return result.allMetrics;
  }

  /**
   * Legacy method for backward compatibility - now defaults to primary location  
   * @deprecated Use getInventoryByLocation() instead
   */
  async getInventory(locationId?: number): Promise<{ data: Inventory[]; locations: Location[] }> {
    if (locationId) {
      return this.getInventoryByLocation(locationId);
    }
    
    // Default to primary location for backward compatibility
    const locations = await this.getLocations();
    const primaryLocation = locations.find(loc => loc.is_primary) || locations[0];
    
    if (!primaryLocation) {
      throw new Error('No locations found');
    }
    
    return this.getInventoryByLocation(primaryLocation.id);
  }

  /**
   * Test Katana API connection
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      await this.makeRequest<any>('/test-connection');
      return { success: true, message: 'Connection successful' };
    } catch (error) {
      return {
        success: false,
        message: error instanceof KatanaApiError ? error.message : 'Connection failed',
      };
    }
  }

  /**
   * Save Katana API settings (encrypted per shop)
   */
  async saveSettings(settings: Omit<KatanaSettings, 'connection_status'>): Promise<void> {
    await this.makeRequest('/settings', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  /**
   * Get current Katana API settings
   */
  async getSettings(): Promise<KatanaSettings | null> {
    try {
      return await this.makeRequest<KatanaSettings>('/settings');
    } catch (error) {
      if (error instanceof KatanaApiError && error.code === 'NOT_FOUND') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Delete Katana API settings
   */
  async deleteSettings(): Promise<void> {
    await this.makeRequest('/settings', {
      method: 'DELETE',
    });
  }

  /**
   * Force refresh shop detection (clears cache)
   */
  refreshShopDetection(): void {
    refreshShopDetection();
  }

  /**
   * Get purchase orders for outsourced manufacturing
   */
  async getOutsourcedPurchaseOrders(
    page = 1,
    perPage = 25
  ): Promise<PaginatedResponse<PurchaseOrder>> {
    return this.getPurchaseOrders(
      { entity_type: ['outsourced'] },
      { field: 'order_created_date', direction: 'desc' },
      page,
      perPage
    );
  }

  /**
   * Search purchase orders by order number or supplier name
   */
  async searchPurchaseOrders(
    query: string,
    page = 1,
    perPage = 25
  ): Promise<PaginatedResponse<PurchaseOrder>> {
    return this.getPurchaseOrders(
      { search: query },
      { field: 'order_created_date', direction: 'desc' },
      page,
      perPage
    );
  }

  /**
   * Search purchase orders by query string
   */
  async searchPurchaseOrdersByQuery(
    query: string,
    page = 1,
    perPage = 25
  ): Promise<PaginatedResponse<PurchaseOrder>> {
    const params = new URLSearchParams({
      q: query,
      page: page.toString(),
      per_page: perPage.toString(),
    });

    return this.makeRequest<PaginatedResponse<PurchaseOrder>>(
      `/purchase-orders/search?${params.toString()}`
    );
  }

  // Manufacturing Order Methods

  /**
   * Get manufacturing order metrics and summary data
   */
  async getManufacturingOrderMetrics(
    dateFrom?: string,
    dateTo?: string,
    maxRecords?: number
  ): Promise<ManufacturingOrderMetrics> {
    const params = new URLSearchParams();
    
    if (dateFrom) {
      params.append('date_from', dateFrom);
    }
    if (dateTo) {
      params.append('date_to', dateTo);
    }
    if (maxRecords) {
      params.append('max_records', maxRecords.toString());
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/manufacturing-orders/metrics?${queryString}` : '/manufacturing-orders/metrics';
    
    return this.makeRequest<ManufacturingOrderMetrics>(endpoint);
  }

  /**
   * Get manufacturing orders with filtering and pagination
   */
  async getManufacturingOrders(
    filters?: ManufacturingOrderFilters,
    _sort?: ManufacturingOrderSortOptions,
    page = 1,
    perPage = 25,
    dateFrom?: string,
    dateTo?: string
  ): Promise<PaginatedResponse<ManufacturingOrder>> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    // Add filters - only use status since it's the only supported parameter per Katana API docs
    if (filters) {
      if (filters.status?.length) {
        // Katana API expects single status value, not array
        params.append('status', filters.status[0]);
      }
    }

    // Add date filtering if provided
    if (dateFrom) {
      params.append('date_from', dateFrom);
    }
    if (dateTo) {
      params.append('date_to', dateTo);
    }

    // Note: sort parameters are not supported by Katana API, removing them

    return this.makeRequest<PaginatedResponse<ManufacturingOrder>>(
      `/manufacturing-orders?${params.toString()}`
    );
  }

  /**
   * Get a specific manufacturing order by ID
   */
  async getManufacturingOrder(id: number): Promise<ManufacturingOrder> {
    return this.makeRequest<ManufacturingOrder>(`/manufacturing-orders/${id}`);
  }

  /**
   * Create a new manufacturing order
   */
  async createManufacturingOrder(data: {
    variant_id: number;
    planned_quantity: number;
    location_id: number;
    production_deadline_date?: string;
    additional_info?: string;
  }): Promise<ManufacturingOrder> {
    return this.makeRequest<ManufacturingOrder>('/manufacturing-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update a manufacturing order
   */
  async updateManufacturingOrder(
    id: number,
    data: Partial<{
      planned_quantity: number;
      actual_quantity: number;
      production_deadline_date: string;
      additional_info: string;
      status: 'NOT_STARTED' | 'BLOCKED' | 'IN_PROGRESS' | 'DONE';
    }>
  ): Promise<ManufacturingOrder> {
    return this.makeRequest<ManufacturingOrder>(`/manufacturing-orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a manufacturing order
   */
  async deleteManufacturingOrder(id: number): Promise<void> {
    return this.makeRequest<void>(`/manufacturing-orders/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Search manufacturing orders by query string
   */
  async searchManufacturingOrders(
    query: string,
    page = 1,
    perPage = 25
  ): Promise<PaginatedResponse<ManufacturingOrder>> {
    const params = new URLSearchParams({
      q: query,
      page: page.toString(),
      per_page: perPage.toString(),
    });

    return this.makeRequest<PaginatedResponse<ManufacturingOrder>>(
      `/manufacturing-orders/search?${params.toString()}`
    );
  }

  /**
   * Get comprehensive inventory analytics report
   */
  async getInventoryAnalytics(options?: {
    locationIds?: number[];
    stockStatus?: string[];
    includeSuggestions?: boolean;
    includeAlerts?: boolean;
    includeManufacturing?: boolean;
    page?: number;
    perPage?: number;
    getAllPages?: boolean;
  }): Promise<any> {
    const params = new URLSearchParams();
    
    if (options?.locationIds?.length) {
      params.append('location_ids', options.locationIds.join(','));
    }
    if (options?.stockStatus?.length) {
      params.append('stock_status', options.stockStatus.join(','));
    }
    if (options?.includeSuggestions) {
      params.append('include_suggestions', 'true');
    }
    if (options?.includeAlerts) {
      params.append('include_alerts', 'true');
    }
    if (options?.includeManufacturing) {
      params.append('include_manufacturing', 'true');
    }

    // Add pagination parameters
    const page = options?.page || 1;
    const perPage = options?.perPage || 250; // Use higher default to get more data
    params.append('page', page.toString());
    params.append('per_page', perPage.toString());

    const queryString = params.toString();
    const endpoint = `/inventory-analytics?${queryString}`;
    
    // If getAllPages is true, fetch all pages
    if (options?.getAllPages) {
      let allData: any = null;
      let currentPage = 1;
      const maxPages = 25; // Safety limit to prevent infinite loops
      
      console.log('🔄 Fetching all inventory analytics pages...');
      
      while (currentPage <= maxPages) {
        const pageParams = new URLSearchParams(params);
        pageParams.set('page', currentPage.toString());
        const pageEndpoint = `/inventory-analytics?${pageParams.toString()}`;
        
        console.log(`📄 Fetching page ${currentPage}...`);
        const pageData = await this.makeRequest<any>(pageEndpoint);
        
        if (!pageData || !pageData.analytics || pageData.analytics.length === 0) {
          console.log(`✅ No more data on page ${currentPage}, stopping`);
          break;
        }
        
        if (currentPage === 1) {
          // First page, use as base
          allData = pageData;
        } else {
          // Subsequent pages, append analytics data
          allData.analytics = [...allData.analytics, ...pageData.analytics];
          
          // Update summary to reflect total across all pages
          if (pageData.summary) {
            allData.summary.total_variants = Math.max(
              allData.summary.total_variants || 0,
              pageData.summary.total_variants || 0
            );
          }
        }
        
        console.log(`📊 Page ${currentPage}: ${pageData.analytics.length} items (total: ${allData.analytics.length})`);
        
        // If we got fewer items than perPage, we've reached the end
        if (pageData.analytics.length < perPage) {
          console.log('✅ Reached end of data (partial page)');
          break;
        }
        
        currentPage++;
        
        // Add small delay between requests to be API-friendly
        if (currentPage <= maxPages) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      console.log(`✅ Fetched all pages: ${allData?.analytics?.length || 0} total items`);
      return allData;
    }
    
    return this.makeRequest<any>(endpoint);
  }

  // =============================================================================
  // ADDITIONAL KATANA API METHODS
  // =============================================================================

  /**
   * Get customers with filtering and pagination
   */
  async getCustomers(
    filters?: CustomerFilters,
    sort?: CustomerSortOptions,
    page = 1,
    perPage = 25
  ): Promise<PaginatedResponse<Customer>> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    // Add filters
    if (filters) {
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.email) {
        params.append('email', filters.email);
      }
      if (filters.phone) {
        params.append('phone', filters.phone);
      }
      if (filters.date_from) {
        params.append('date_from', filters.date_from);
      }
      if (filters.date_to) {
        params.append('date_to', filters.date_to);
      }
    }

    // Add sorting
    if (sort) {
      params.append('sort_by', sort.field);
      params.append('sort_direction', sort.direction);
    }

    return this.makeRequest<PaginatedResponse<Customer>>(
      `/customers?${params.toString()}`
    );
  }

  /**
   * Get a specific customer by ID
   */
  async getCustomer(id: number): Promise<Customer> {
    return this.makeRequest<Customer>(`/customers/${id}`);
  }

  /**
   * Get sales orders with filtering and pagination
   */
  async getSalesOrders(
    filters?: SalesOrderFilters,
    sort?: SalesOrderSortOptions,
    page = 1,
    perPage = 25
  ): Promise<PaginatedResponse<SalesOrder>> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    // Add filters
    if (filters) {
      if (filters.status?.length) {
        params.append('status', filters.status.join(','));
      }
      if (filters.customer_id?.length) {
        params.append('customer_id', filters.customer_id.join(','));
      }
      if (filters.location_id?.length) {
        params.append('location_id', filters.location_id.join(','));
      }
      if (filters.currency?.length) {
        params.append('currency', filters.currency.join(','));
      }
      if (filters.date_from) {
        params.append('date_from', filters.date_from);
      }
      if (filters.date_to) {
        params.append('date_to', filters.date_to);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
    }

    // Add sorting
    if (sort) {
      params.append('sort_by', sort.field);
      params.append('sort_direction', sort.direction);
    }

    return this.makeRequest<PaginatedResponse<SalesOrder>>(
      `/sales-orders?${params.toString()}`
    );
  }

  /**
   * Get a specific sales order by ID
   */
  async getSalesOrder(id: number): Promise<SalesOrder> {
    return this.makeRequest<SalesOrder>(`/sales-orders/${id}`);
  }


  /**
   * Get sales order fulfillments for a specific sales order
   */
  async getSalesOrderFulfillments(salesOrderId: number): Promise<SalesOrderFulfillment[]> {
    return this.makeRequest<SalesOrderFulfillment[]>(
      `/sales-orders/${salesOrderId}/fulfillments`
    );
  }

  /**
   * Get sales returns with filtering
   */
  async getSalesReturns(
    salesOrderId?: number,
    status?: 'PENDING' | 'APPROVED' | 'REJECTED',
    page = 1,
    perPage = 25
  ): Promise<PaginatedResponse<SalesReturn>> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    if (salesOrderId) {
      params.append('sales_order_id', salesOrderId.toString());
    }
    if (status) {
      params.append('status', status);
    }

    return this.makeRequest<PaginatedResponse<SalesReturn>>(
      `/sales-returns?${params.toString()}`
    );
  }
}

// Custom error class for Katana API errors
export class KatanaApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'KatanaApiError';
  }
}

// Export singleton instance
// Create and export a singleton instance
let katanaApiInstance: KatanaApiService | null = null;

export const katanaApi = (() => {
  if (!katanaApiInstance) {
    katanaApiInstance = new KatanaApiService();
    // Make it globally available for debugging and shop detection refresh
    (window as any).katanaService = katanaApiInstance;
  }
  return katanaApiInstance;
})();
export default katanaApi;
